<template>
  <v-row>
    <v-col cols="12">
      <v-card class="pur-card" flat>
        <!-- ================= HEADER BAND ================= -->
        <div class="pur-header pl-base-bg">
          <div class="pur-header-left">
            <div class="pur-header-icon">
              <v-icon color="#fff" size="22">mdi-cart-outline</v-icon>
            </div>
            <div>
              <div class="pur-title">Bill #{{ purchase?.billNumber }}</div>
              <div class="pur-subtitle">
                {{ purchase?.vendor?.name }} &middot; {{ formatDate(purchase?.billDate) }}
              </div>
            </div>
          </div>

          <div class="pur-header-right">
            <v-chip
              :color="statusColor(purchase?.status)"
              dark
              small
              class="text-uppercase font-weight-bold pur-status-chip"
            >
              {{ purchase?.status }}
            </v-chip>

            <div class="pur-actions">
              <v-tooltip v-if="document" bottom>
                <template #activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    small
                    class="pur-action-btn pur-action-btn--view"
                    :href="document.fileUrl"
                    target="_blank"
                  >
                    <v-icon small color="#fff">mdi-eye</v-icon>
                  </v-btn>
                </template>
                <span>View bill / receipt</span>
              </v-tooltip>

              <v-btn
                v-if="!isViewOnly"
                small
                class="pur-edit-btn"
                :to="`/purchases/${purchase?._id}/edit`"
              >
                Edit
              </v-btn>

              <v-btn
                v-if="!isViewOnly"
                small
                class="pur-debit-note-btn"
                :disabled="!canIssueDebitNote"
                :to="`/debit-notes/create?purchaseId=${purchase?._id}`"
              >
                Issue Debit Note
              </v-btn>

              <v-btn
                v-if="!isViewOnly && purchase?.status !== 'PAID' && purchase?.status !== 'CANCELLED'"
                small
                class="pur-pay-btn"
                @click="openPaymentDialog"
              >
                Mark as Paid
              </v-btn>
            </div>
          </div>
        </div>

        <div class="pa-6">
          <!-- ================= VENDOR ================= -->
          <v-row>
            <v-col cols="12" md="6">
              <div class="pur-party-card">
                <div class="pur-party-label">Vendor</div>
                <div class="pur-party-name">{{ purchase?.vendor?.name }}</div>
                <div class="pur-party-line" v-if="purchase?.vendor?.address">
                  {{ purchase?.vendor?.address }}
                </div>
                <div class="pur-party-line" v-if="purchase?.vendor?.gstin">
                  GSTIN: {{ purchase?.vendor?.gstin }}
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- ================= META ================= -->
          <div class="pur-meta-strip">
            <div class="pur-meta-item">
              <div class="pur-meta-label">Bill Date</div>
              <div class="pur-meta-value">{{ formatDate(purchase?.billDate) }}</div>
            </div>

            <div class="pur-meta-divider" />

            <div class="pur-meta-item">
              <div class="pur-meta-label">Items</div>
              <div class="pur-meta-value">{{ purchase?.items?.length || 0 }}</div>
            </div>
          </div>

          <!-- ================= ITEMS ================= -->
          <div class="pur-items-wrap">
            <div class="table-scroll">
              <v-simple-table dense class="pur-items-table">
                <thead>
                  <tr>
                    <th class="text-left">Description</th>
                    <th class="text-right">HSN / SAC</th>
                    <th class="text-right">Qty</th>
                    <th class="text-right">Rate</th>
                    <th class="text-right">Tax %</th>
                    <th class="text-center">ITC Eligible</th>
                    <th class="text-right">Amount</th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-for="(item, i) in purchase?.items" :key="i">
                    <td>{{ item.description }}</td>
                    <td class="text-right">{{ item.hsn }}</td>
                    <td class="text-right">{{ item.quantity }}</td>
                    <td class="text-right">{{ formatAmount(item.price) }}</td>
                    <td class="text-right">{{ item.taxPercent }}%</td>
                    <td class="text-center">{{ item.isItcEligible ? "Yes" : "No" }}</td>
                    <td class="text-right font-weight-medium">
                      {{ formatAmount(item.amount) }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </div>

          <!-- ================= TOTAL ================= -->
          <v-row justify="end" class="totals">
            <v-col cols="12" md="4">
              <div class="pur-totals-card">
                <v-row dense>
                  <v-col class="grey--text py-1">Subtotal</v-col>
                  <v-col class="text-right py-1">{{ formatAmount(purchase?.subtotal) }}</v-col>
                </v-row>

                <template v-if="purchase?.isInterState">
                  <v-row dense>
                    <v-col class="grey--text py-1">IGST</v-col>
                    <v-col class="text-right py-1">{{ formatAmount(purchase?.igst) }}</v-col>
                  </v-row>
                </template>
                <template v-else>
                  <v-row dense>
                    <v-col class="grey--text py-1">CGST</v-col>
                    <v-col class="text-right py-1">{{ formatAmount(purchase?.cgst) }}</v-col>
                  </v-row>
                  <v-row dense>
                    <v-col class="grey--text py-1">SGST</v-col>
                    <v-col class="text-right py-1">{{ formatAmount(purchase?.sgst) }}</v-col>
                  </v-row>
                </template>

                <template v-if="purchase?.status === 'PARTIALLY_PAID'">
                  <v-row dense class="mt-1">
                    <v-col class="grey--text py-1">Amount Paid</v-col>
                    <v-col class="text-right py-1 green--text font-weight-medium">
                      {{ formatAmount(purchase?.amountPaid) }}
                    </v-col>
                  </v-row>
                </template>

                <v-row v-if="purchase?.debitedAmount > 0" dense>
                  <v-col class="grey--text py-1">Debited</v-col>
                  <v-col class="text-right py-1 blue--text font-weight-medium">
                    {{ formatAmount(purchase?.debitedAmount) }}
                  </v-col>
                </v-row>

                <!-- Already fully paid, then a debit note reduced the
                     purchase's value afterwards — the business paid more
                     than the purchase is now worth, so this is a refund
                     owed TO the business, not an amount still owed to the
                     vendor. Distinct from "Balance Due" below, which only
                     applies while payment is still pending. -->
                <v-row v-if="purchase?.status === 'PAID' && purchase?.debitedAmount > 0" dense>
                  <v-col class="grey--text py-1">Refund Due</v-col>
                  <v-col class="text-right py-1 orange--text text--darken-2 font-weight-medium">
                    {{ formatAmount(purchase.debitedAmount) }}
                  </v-col>
                </v-row>

                <v-row v-if="purchase?.status === 'PARTIALLY_PAID'" dense>
                  <v-col class="grey--text py-1">Balance Due</v-col>
                  <v-col class="text-right py-1 red--text font-weight-medium">
                    {{ formatAmount(remainingBalance) }}
                  </v-col>
                </v-row>

                <v-divider class="my-2" />

                <v-row dense class="text-h6 font-weight-bold">
                  <v-col>Total</v-col>
                  <v-col class="text-right pur-total-value">
                    {{ formatAmount(purchase?.totalAmount) }}
                  </v-col>
                </v-row>
              </div>
            </v-col>
          </v-row>

          <!-- ================= PAYMENT HISTORY ================= -->
          <v-row v-if="purchase?.payments?.length" class="mt-2">
            <v-col cols="12">
              <div class="pur-section-label">Payment History</div>

              <div class="pur-items-wrap">
                <div class="table-scroll">
                  <v-simple-table dense class="pur-items-table">
                    <thead>
                      <tr>
                        <th class="text-left">Date</th>
                        <th class="text-left">Method</th>
                        <th class="text-right">Amount</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr v-for="(payment, i) in purchase.payments" :key="i">
                        <td>{{ formatDate(payment.paidAt) }}</td>
                        <td>{{ formatPaymentMethod(payment.paymentMethod) }}</td>
                        <td class="text-right font-weight-medium">
                          {{ formatAmount(payment.amount) }}
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- ================= NOTE ================= -->
          <v-row v-if="purchase?.note" class="mt-4">
            <v-col>
              <v-alert type="info" outlined dense>
                {{ purchase.note }}
              </v-alert>
            </v-col>
          </v-row>
        </div>
      </v-card>
    </v-col>

    <!-- PAYMENT DIALOG -->
    <global-dialog
      v-if="paymentDialog"
      v-model="paymentDialog"
      :title="`Pay Vendor - ${purchase?.billNumber}`"
      :buttons="paymentDialogButtons"
    >
      <v-row dense>
        <v-col cols="12" class="mt-3">
          <v-alert type="info" dense text>
            You can enter a partial amount to record a partial payment. The remaining balance will stay as due.
          </v-alert>
        </v-col>
        <v-col cols="12">
          <v-text-field
            v-model="paymentAmount"
            label="Amount"
            type="number"
            outlined
            dense
            :hint="`Balance due: ${formatAmount(remainingBalance)}`"
            persistent-hint
            :error-messages="
              paymentAmount && !validPaymentAmount
                ? `Amount must be between 1 and ${formatAmount(remainingBalance)}`
                : ''
            "
          />

          <v-select
            v-model="paymentMethod"
            :items="paymentMethods"
            label="Payment Method"
            outlined
            dense
            required
            class="mt-4"
          />
        </v-col>
      </v-row>
    </global-dialog>
  </v-row>
</template>

<script>
import { mapActions } from "vuex";
import GlobalDialog from "@/components/common/global-dialog.vue";

export default {
  name: "ViewPurchase",
  components: { GlobalDialog },

  data() {
    return {
      purchase: null,
      document: null,

      paymentDialog: false,
      paymentMethod: "",
      paymentAmount: 0,
      paymentMethods: [
        "CASH",
        "BANK_TRANSFER",
        "UPI",
        "DEBIT_CARD",
        "CREDIT_CARD",
        "CHEQUE",
        "NET_BANKING",
        "WALLET",
        "OTHER",
      ],
    };
  },

  async created() {
    await this.fetchPurchase();
  },

  computed: {
    remainingBalance() {
      return (
        (this.purchase?.totalAmount || 0) -
        (this.purchase?.amountPaid || 0) -
        (this.purchase?.debitedAmount || 0)
      );
    },

    canIssueDebitNote() {
      if (!this.purchase) return false;
      if (this.purchase.status === "CANCELLED") return false;
      const remaining =
        (this.purchase.totalAmount || 0) - (this.purchase.debitedAmount || 0);
      return remaining > 0.01;
    },

    validPaymentAmount() {
      const v = parseFloat(this.paymentAmount);
      return !isNaN(v) && v > 0 && v <= this.remainingBalance;
    },

    paymentDialogButtons() {
      return [
        {
          text: "Cancel",
          color: "secondary",
          action: this.closePaymentDialog,
        },
        {
          text: "Confirm",
          color: "primary",
          disabled: !this.paymentMethod || !this.validPaymentAmount,
          action: this.confirmPayment,
        },
      ];
    },
  },

  methods: {
    ...mapActions("purchases", ["getPurchaseById", "markPurchaseAsPaid"]),

    async fetchPurchase() {
      try {
        const id = this.$route.params.id;
        const res = await this.getPurchaseById(id);

        this.purchase = res.purchase || res;
        this.document = res.document || null;
      } catch (err) {
        this.$toast?.error("Failed to load purchase");
      }
    },

    formatDate(date) {
      if (!date) return "-";

      return new Date(date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },

    formatAmount(value) {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }).format(value || 0);
    },

    formatPaymentMethod(method) {
      if (!method) return "-";
      return method
        .toLowerCase()
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    },

    statusColor(status) {
      switch (status) {
        case "PAID":
          return "green";
        case "PARTIALLY_PAID":
          return "blue";
        case "CANCELLED":
          return "red";
        default:
          return "orange";
      }
    },

    openPaymentDialog() {
      this.paymentMethod = "";
      this.paymentAmount = this.remainingBalance;
      this.paymentDialog = true;
    },

    closePaymentDialog() {
      this.paymentDialog = false;
      this.paymentMethod = "";
      this.paymentAmount = 0;
    },

    async confirmPayment() {
      try {
        const res = await this.markPurchaseAsPaid({
          id: this.purchase._id,
          paymentMethod: this.paymentMethod,
          amount: parseFloat(this.paymentAmount),
        });

        const updated = res?.purchase;
        if (updated) {
          this.purchase = updated;
        } else {
          this.purchase.status = "PAID";
          this.purchase.amountPaid = this.purchase.totalAmount;
        }

        this.$toast?.success(res?.message || "Payment recorded");
        this.closePaymentDialog();
      } catch (err) {
        console.error(err);
        this.$toast?.error(
          err?.response?.data?.message || "Failed to update status",
        );
      }
    },
  },
};
</script>

<style scoped>
.pur-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

/* ================= HEADER BAND ================= */
.pur-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 20px;
}

.pur-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.pur-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.pur-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.pur-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.pur-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.pur-status-chip {
  letter-spacing: 0.04em;
}

.pur-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pur-action-btn {
  border: 2px solid rgba(255, 255, 255, 0.6);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15) !important;
}

.pur-action-btn--view {
  background: #f59e0b !important;
}
.pur-action-btn--view:hover {
  background: #d97e06 !important;
}

.pur-edit-btn {
  background: rgba(255, 255, 255, 0.16) !important;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #ffffff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.pur-edit-btn:hover {
  background: rgba(255, 255, 255, 0.26) !important;
}

.pur-pay-btn {
  background: rgba(255, 255, 255, 0.16) !important;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #ffffff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.pur-pay-btn:hover {
  background: rgba(255, 255, 255, 0.26) !important;
}

.pur-pay-btn.v-btn--disabled {
  background: rgba(255, 255, 255, 0.08) !important;
  color: rgba(255, 255, 255, 0.5) !important;
}

.pur-debit-note-btn {
  background: rgba(255, 255, 255, 0.16) !important;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #ffffff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.pur-debit-note-btn:hover {
  background: rgba(255, 255, 255, 0.26) !important;
}

.pur-debit-note-btn.v-btn--disabled {
  background: rgba(255, 255, 255, 0.08) !important;
  color: rgba(255, 255, 255, 0.5) !important;
}

/* ================= PARTY CARD ================= */
.pur-party-card {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 16px 18px;
  height: 100%;
}

.pur-party-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 6px;
}

.pur-party-name {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 4px;
}

.pur-party-line {
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
  display: flex;
  align-items: center;
}

/* ================= META STRIP ================= */
.pur-meta-strip {
  display: flex;
  align-items: center;
  margin: 20px 0;
  padding: 14px 18px;
  border-radius: 0;
  background: #fbfbfd;
  border: 1px solid #eef1f5;
}

.pur-meta-item {
  flex: 1;
}

.pur-meta-divider {
  width: 1px;
  height: 32px;
  background: #e5e9ef;
  margin: 0 20px;
}

.pur-meta-label {
  font-size: 11px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
}

.pur-meta-value {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

@media (max-width: 600px) {
  .pur-meta-strip {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .pur-meta-divider {
    width: 100%;
    height: 1px;
    margin: 0;
  }

  .pur-header {
    padding: 18px 16px;
  }

  .pa-6 {
    padding: 16px !important;
  }
}

/* ================= ITEMS TABLE ================= */
.pur-items-wrap {
  border: 1px solid #eef1f5;
  border-radius: 0;
  overflow: auto;
}

.table-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.pur-items-table {
  min-width: 640px;
}

.pur-items-table >>> th {
  background: #f8fafc;
  font-weight: 700 !important;
  font-size: 12px !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b !important;
}

.pur-items-table >>> tbody tr:hover {
  background: #f8fafc;
}

/* ================= TOTALS ================= */
.totals {
  margin-top: 20px;
}

.pur-totals-card {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 16px 18px;
}

.pur-total-value {
  color: #0a5a90;
}

/* ================= PAYMENT HISTORY ================= */
.pur-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 10px;
}
</style>
